import React from "react";

import { Label, SelectItem } from "@fronterahealth/frontera-ui-components";

import { ApiPassageGoalTypeChoices } from "@api/graphql/types-and-hooks";
import { InlineEditField } from "@components/ProgrammingReview/InlineEditField";
import { useInlineGoalEdit } from "@components/ProgrammingReview/hooks/useInlineEdit";
import { useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface ProgrammingReviewGoalFormProps {
  isReadOnly?: boolean;
}

const GOAL_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiPassageGoalTypeChoices.Behavior, primary: "Behavior" },
  { id: ApiPassageGoalTypeChoices.Skill, primary: "Skill" },
];
export const ProgrammingReviewGoalForm: React.FC<ProgrammingReviewGoalFormProps> = ({ isReadOnly = false }) => {
  const { selectedProgramReviewGoal } = useProgrammingReviewData();

  const { saveGoalFields } = useInlineGoalEdit({
    goalId: selectedProgramReviewGoal?.id || "",
  });

  const isBehaviorGoal = selectedProgramReviewGoal?.type === ApiPassageGoalTypeChoices.Behavior;
  const isSkillGoal = selectedProgramReviewGoal?.type === ApiPassageGoalTypeChoices.Skill;

  return (
    <div className="flex flex-col gap-5 w-full">
      <div
        key={selectedProgramReviewGoal?.id || "no-goal"}
        className="flex border border-limestone-200 rounded-2xl flex-col bg-white w-full"
      >
        <div className="flex items-center justify-between p-4 px-6 rounded-t-2xl cursor-pointer border-b border-limestone-100">
          <div className="flex items-center gap-2 flex-1">
            <Label size="medium" colorType="primary" displayType="loud">
              Goal Details
            </Label>
          </div>
        </div>
        <div className="p-6 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <InlineEditField
              key="goal-title"
              value={selectedProgramReviewGoal?.title || ""}
              fieldKey="title"
              onSave={saveGoalFields}
              title="Goal Name"
              isReadOnly={isReadOnly}
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <InlineEditField
              key="goal-type"
              title="Goal Type"
              inputType="select"
              selectOptions={GOAL_TYPE_OPTIONS}
              value={selectedProgramReviewGoal?.type || ""}
              fieldKey="type"
              onSave={saveGoalFields}
              isReadOnly={isReadOnly}
            />
          </div>

          <div className="flex flex-col gap-2">
            <InlineEditField
              key="goal-description"
              value={selectedProgramReviewGoal?.description || ""}
              fieldKey="description"
              onSave={saveGoalFields}
              title="Description"
              isReadOnly={isReadOnly}
            />
          </div>
        </div>
      </div>

      {/* Behavior Details - only show if goal type is Behavior */}
      {isBehaviorGoal && (
        <div className="flex border border-limestone-200 rounded-2xl flex-col bg-white w-full">
          <div className="flex items-center justify-between p-4 px-6 rounded-t-2xl cursor-pointer border-b border-limestone-100">
            <div className="flex items-center gap-2 flex-1">
              <Label size="medium" colorType="primary" displayType="loud">
                Behavior Details
              </Label>
            </div>
          </div>

          <div className="p-6 flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-operationalDefinition"
                value={selectedProgramReviewGoal?.operationalDefinition || ""}
                fieldKey="operationalDefinition"
                onSave={saveGoalFields}
                title="Operational Definition"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-onset"
                value={selectedProgramReviewGoal?.onset || ""}
                fieldKey="onset"
                onSave={saveGoalFields}
                title="Onset"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-offset"
                value={selectedProgramReviewGoal?.offset || ""}
                fieldKey="offset"
                onSave={saveGoalFields}
                title="Offset"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-hypothesizedFunction"
                value={selectedProgramReviewGoal?.hypothesizedFunction || ""}
                fieldKey="hypothesizedFunction"
                onSave={saveGoalFields}
                title="Hypothesized Function"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-errorConsequenceStrategy"
                value={selectedProgramReviewGoal?.errorConsequenceStrategy || ""}
                fieldKey="errorConsequenceStrategy"
                onSave={saveGoalFields}
                title="Error Consequence Strategy"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-replacementBehavior"
                value={selectedProgramReviewGoal?.replacementBehavior || ""}
                fieldKey="replacementBehavior"
                onSave={saveGoalFields}
                title="Replacement Behavior"
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
        </div>
      )}

      {/* Skill Details - only show if goal type is Skill */}
      {isSkillGoal && (
        <div className="flex border border-limestone-200 rounded-2xl flex-col bg-white w-full">
          <div className="flex items-center justify-between p-4 px-6 rounded-t-2xl cursor-pointer border-b border-limestone-100">
            <div className="flex items-center gap-2 flex-1">
              <Label size="medium" colorType="primary" displayType="loud">
                Skill Details
              </Label>
            </div>
          </div>

          <div className="p-6 flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-teachingMethod"
                value={selectedProgramReviewGoal?.teachingMethod || ""}
                fieldKey="teachingMethod"
                onSave={saveGoalFields}
                title="Teaching Method"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-discriminativeStimulus"
                value={selectedProgramReviewGoal?.discriminativeStimulus || ""}
                fieldKey="discriminativeStimulus"
                onSave={saveGoalFields}
                title="Discriminative Stimulus"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-promptingInstructions"
                value={selectedProgramReviewGoal?.promptingInstructions || ""}
                fieldKey="promptingInstructions"
                onSave={saveGoalFields}
                title="Prompting Instructions"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-expectedResponse"
                value={selectedProgramReviewGoal?.expectedResponse || ""}
                fieldKey="expectedResponse"
                onSave={saveGoalFields}
                title="Expected Response"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-reinforcementStrategy"
                value={selectedProgramReviewGoal?.reinforcementStrategy || ""}
                fieldKey="reinforcementStrategy"
                onSave={saveGoalFields}
                title="Reinforcement Strategy"
                isReadOnly={isReadOnly}
              />
            </div>

            <div className="flex flex-col gap-2">
              <InlineEditField
                key="goal-errorCorrectionStrategy"
                value={selectedProgramReviewGoal?.errorCorrectionStrategy || ""}
                fieldKey="errorCorrectionStrategy"
                onSave={saveGoalFields}
                title="Error Correction Strategy"
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
